import {
  ApolloClient,
  ApolloProvider,
  createHttpLink,
  FetchResult,
  InMemoryCache,
  LazyQueryExecFunction,
  MutationFunctionOptions,
  NormalizedCacheObject,
} from '@apollo/client';
import { FCWChildren } from 'apollo/FCWithChildren';
import {
  Exact,
  GetComponentQuery,
  GetGenericActionsQuery,
  GetLegalDocumentByIdQuery,
  GetLegalDocumentsQuery,
  GetPageQuery,
  GetPagesQuery,
  GetTemplateMessagesQuery,
  InputMaybe,
  Scalars,
  UpdateLegalDocumentByIdMutation,
  useGetComponentLazyQuery,
  useGetGenericActionsLazyQuery,
  useGetLegalDocumentByIdLazyQuery,
  useGetLegalDocumentsLazyQuery,
  useGetPageLazyQuery,
  useGetPagesLazyQuery,
  useGetTemplateMessagesLazyQuery,
  useUpdateLegalDocumentByIdMutation,
} from 'graphql/generated/strapi';
import React, { createContext, useContext } from 'react';
import { getStrapiConfig } from 'utilities/config';
import { setContext } from '@apollo/client/link/context';
import { getTokenFromFirebase } from '../firebase/hooks';

type StrapiContextType = {
  client: ApolloClient<NormalizedCacheObject>;
  getLegalDocuments: LazyQueryExecFunction<
    GetLegalDocumentsQuery,
    Exact<{
      locale?: InputMaybe<Scalars['I18NLocaleCode']>;
      legalDocumentTypes?: InputMaybe<
        Array<InputMaybe<Scalars['String']>> | InputMaybe<Scalars['String']>
      >;
    }>
  >;
  getComponent: LazyQueryExecFunction<
    GetComponentQuery,
    Exact<{
      locale?: InputMaybe<Scalars['I18NLocaleCode']>;
      componentId?: InputMaybe<Scalars['Long']>;
    }>
  >;
  getLegalDocumentById: LazyQueryExecFunction<
    GetLegalDocumentByIdQuery,
    Exact<{
      locale?: InputMaybe<Scalars['I18NLocaleCode']>;
      legalDocumentId: Scalars['Long'];
    }>
  >;
  getGenericActions: LazyQueryExecFunction<
    GetGenericActionsQuery,
    Exact<{
      locale?: InputMaybe<Scalars['I18NLocaleCode']>;
      genericActionId?: InputMaybe<
        Array<InputMaybe<Scalars['Long']>> | InputMaybe<Scalars['Long']>
      >;
    }>
  >;
  getTemplateMessages: LazyQueryExecFunction<
    GetTemplateMessagesQuery,
    Exact<{
      locale?: InputMaybe<Scalars['I18NLocaleCode']>;
      templateId?: InputMaybe<
        Array<InputMaybe<Scalars['Long']>> | InputMaybe<Scalars['Long']>
      >;
    }>
  >;
  getPage: LazyQueryExecFunction<
    GetPageQuery,
    Exact<{
      locale?: InputMaybe<Scalars['I18NLocaleCode']>;
      pageId?: InputMaybe<Scalars['Long']>;
    }>
  >;
  getPages: LazyQueryExecFunction<
    GetPagesQuery,
    Exact<{
      locale?: InputMaybe<Scalars['I18NLocaleCode']>;
    }>
  >;
  updateLegalDocById: (
    options?: MutationFunctionOptions<
      UpdateLegalDocumentByIdMutation,
      Exact<{ locale?: string; legalDocumentId: string; contentWeb: string }>
    >,
  ) => Promise<
    FetchResult<
      UpdateLegalDocumentByIdMutation,
      Record<string, unknown>,
      Record<string, unknown>
    >
  >;
};

const strapiContext = createContext<StrapiContextType>({} as StrapiContextType);

const { strapiGraphQlApi } = getStrapiConfig();

const httpLink = createHttpLink({
  uri: strapiGraphQlApi,
});

const authLink = setContext(async (_, { headers }) => {
  const token = await getTokenFromFirebase();

  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache({
    addTypename: false,
  }),
});

export const useStrapi = () => {
  const ctx = useContext(strapiContext);
  if (!ctx.client && process.env.REACT_APP_IS_DEBUG_MODE)
    throw new Error('useStrapi must be called from within a LocalesProvider');
  return ctx;
};

const StrapiProvider: FCWChildren = ({ children }) => {
  const [getLegalDocuments] = useGetLegalDocumentsLazyQuery({ client });
  const [getLegalDocumentById] = useGetLegalDocumentByIdLazyQuery({
    client,
  });
  const [getComponent] = useGetComponentLazyQuery({ client });
  const [getGenericActions] = useGetGenericActionsLazyQuery({ client });
  const [getTemplateMessages] = useGetTemplateMessagesLazyQuery({ client });
  const [getPage] = useGetPageLazyQuery({ client });
  const [getPages] = useGetPagesLazyQuery({ client });
  const [updateLegalDocById] = useUpdateLegalDocumentByIdMutation({ client });
  return (
    <strapiContext.Provider
      value={{
        client,
        getLegalDocuments,
        getComponent,
        getLegalDocumentById,
        getGenericActions,
        getTemplateMessages,
        getPage,
        getPages,
        updateLegalDocById,
      }}
    >
      {children}
    </strapiContext.Provider>
  );
};

export const LocalesProvider: FCWChildren = ({ children }) => {
  return (
    <ApolloProvider client={client}>
      <StrapiProvider>{children}</StrapiProvider>
    </ApolloProvider>
  );
};
